import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Crown, User, ChevronDown, ChevronUp } from 'lucide-react';
import './OnlineUsers.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnlineUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOffline, setShowOffline] = useState(false);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users/online`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch online users:', err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const sendHeartbeat = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/heartbeat`, {}, {
        withCredentials: true
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Heartbeat failed:', err.message);
      }
    }
  }, []);

  useEffect(() => {
    fetchOnlineUsers();
    sendHeartbeat();

    const usersInterval = setInterval(fetchOnlineUsers, 30000);
    const heartbeatInterval = setInterval(sendHeartbeat, 60000);

    return () => {
      clearInterval(usersInterval);
      clearInterval(heartbeatInterval);
    };
  }, [fetchOnlineUsers, sendHeartbeat]);

  const onlineUsers = users.filter(u => u.is_online);
  const offlineUsers = users.filter(u => !u.is_online);

  return (
    <div className="online-users-widget" data-testid="online-users-widget">
      <div className="widget-header">
        <Users size={20} />
        <h3>Team Status</h3>
        <span className="online-count">{onlineUsers.length} online</span>
      </div>

      {loading ? (
        <div className="loading-state">Loading...</div>
      ) : (
        <>
          {/* Online Users Section */}
          <div className="users-section">
            <div className="section-label online-label">
              <span className="pulse-dot"></span>
              Online Now
            </div>
            <div className="users-list">
              {onlineUsers.length === 0 ? (
                <div className="empty-state">No one else is online</div>
              ) : (
                onlineUsers.map(user => (
                  <div key={user.id} className="user-item online" data-testid={`user-${user.employee_id}`}>
                    <div className="user-avatar">
                      {user.role === 'admin' ? <Crown size={16} /> : <User size={16} />}
                      <span className="status-dot online"></span>
                    </div>
                    <div className="user-info">
                      <span className="user-name">{user.name}</span>
                      <span className="user-role">{user.role === 'admin' ? 'Owner' : 'Staff'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Offline Users Section - Collapsible */}
          {offlineUsers.length > 0 && (
            <div className="users-section offline-section">
              <button 
                className="offline-toggle"
                onClick={() => setShowOffline(!showOffline)}
                data-testid="offline-toggle-btn"
              >
                <span>Offline ({offlineUsers.length})</span>
                {showOffline ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              
              {showOffline && (
                <div className="users-list">
                  {offlineUsers.map(user => (
                    <div key={user.id} className="user-item offline">
                      <div className="user-avatar">
                        {user.role === 'admin' ? <Crown size={16} /> : <User size={16} />}
                        <span className="status-dot offline"></span>
                      </div>
                      <div className="user-info">
                        <span className="user-name">{user.name}</span>
                        <span className="user-role">{user.role === 'admin' ? 'Owner' : 'Staff'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OnlineUsers;
